import { memo } from 'react';
import type { AnnotationLinkId, AnnotationLinkStyle, ViewModelId } from 'balus-common';
import type { ModelElementId } from '@view-model/domain/key';
import type { SelectedItemSet } from '@view-model/models/common/basic';
import type { PositionSet } from '@view-model/models/common/PositionSet';
import type { INodeShape, ShapeMap } from '@model-framework/shape';
import { RectShape } from '@model-framework/shape';
import { LinkConstants } from '@model-framework/link';
import { LinkPlacement } from '@view-model/models/sticky/StickyLink/domain/LinkPlacement';
import type { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';
import type { FileContainerCollectionContents } from '@view-model/models/sticky/FileContainer/domain/FileContainerCollectionContents';
import type { DescriptionPanelCollectionContents } from '@view-model/models/sticky/DescriptionPanel/domain/DescriptionPanelCollectionContents';
import type { AnnotationLinkCollection } from '../domain/AnnotationLinkCollection';
import { AnnotationLinkView } from './AnnotationLinkView';

type Props = {
  viewModelId: ViewModelId;
  annotationLinks: AnnotationLinkCollection;
  fileContainerContents: FileContainerCollectionContents;
  panelContents: DescriptionPanelCollectionContents;
  nodePositions: PositionSet;
  zonePositions: PositionSet;
  shapeMap: ShapeMap<INodeShape>;
  selectedItems: SelectedItemSet<ModelElementId>;
  readonly: boolean;
  onClick(id: ModelElementId, e: React.MouseEvent<SVGGElement>): void;
  onStyleSelected(id: AnnotationLinkId, style: AnnotationLinkStyle): void;
  onReverse(id: AnnotationLinkId): void;
  onBendChanged(id: AnnotationLinkId, bend: LinkBend): void;
  onDelete(): void;
};

export const AnnotationLinkCollectionView = memo(
  ({
    viewModelId,
    annotationLinks,
    fileContainerContents,
    panelContents,
    nodePositions,
    zonePositions,
    shapeMap,
    selectedItems,
    readonly,
    onClick,
    onStyleSelected,
    onReverse,
    onBendChanged,
    onDelete,
  }: Props) => (
    <>
      {annotationLinks.all().map((link) => {
        const fromRect =
          link.fromKind === 'DescriptionPanel'
            ? panelContents.getRect(link.fromId)
            : fileContainerContents.getRect(link.fromId);
        const toId = link.to.id.toString();
        const toPosition = nodePositions.find(toId) ?? zonePositions.find(toId);
        const toShape = shapeMap.get(toId);
        if (!fromRect || !toPosition || !toShape) return null;

        const isSelected = selectedItems.isSelected(link.id);
        return (
          <g key={link.id} data-model-element-id={link.id}>
            <AnnotationLinkView
              viewModelId={viewModelId}
              link={link}
              placement={
                new LinkPlacement(
                  fromRect.topLeft(),
                  toPosition,
                  new RectShape({ width: fromRect.width, height: fromRect.height }),
                  toShape,
                  LinkConstants.ConnectionPointMargin
                )
              }
              isSelected={isSelected}
              showMenu={isSelected && selectedItems.isSingleSelected()}
              readonly={readonly}
              // 線は付箋・ゾーンより手前に描くため、選択中の付箋・ゾーンにつながる線がその線の作成ハンドルを覆ってしまう
              passThrough={selectedItems.isSelected(toId)}
              onClick={onClick}
              onStyleSelected={onStyleSelected}
              onReverse={onReverse}
              onBendChanged={onBendChanged}
              onDelete={onDelete}
            />
          </g>
        );
      })}
    </>
  )
);
AnnotationLinkCollectionView.displayName = 'AnnotationLinkCollectionView';
